/**
 * Karbon360° v2 — ⌘K command palette.
 * Commands are derived from the nav model, so the palette can never list a
 * destination the sidebar doesn't have.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from './Icons';
import { useT } from '../../i18n';
import { buildCommands } from './nav-model';
import { useSession } from './session';

export default function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const { isAdmin, hasCbam, resolved } = useSession();
  // `tr` not `t` — the filter below already binds `t` to the lowercased query.
  const { t: tr } = useT();
  // CBAM commands wait for the resolved session, mirroring the sidebar.
  const commands = useMemo(
    () => buildCommands({ isAdmin, cbam: resolved && hasCbam }),
    [isAdmin, hasCbam, resolved]
  );

  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return commands;
    return commands.filter(
      (c) => tr(c.labelKey).toLowerCase().includes(t) || tr(c.groupKey).toLowerCase().includes(t)
    );
  }, [q, commands, tr]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setI(0); }, [q]);

  // Keep the highlighted row in view when navigating with the keyboard.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [i]);

  const run = (cmd) => {
    if (!cmd) return;
    navigate(cmd.to);
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setI((v) => Math.min(v + 1, hits.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setI((v) => Math.max(v - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); run(hits[i]); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  let lastGroup = null;

  return (
    <div className="k2-cp-s" onMouseDown={onClose}>
      <div
        className="k2-cp"
        role="dialog"
        aria-label={tr('chrome.search')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="k2-cp-in">
          <span style={{ color: 'var(--n400)', display: 'grid' }}><X.Search s={17} /></span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr('chrome.search')}
            aria-label={tr('chrome.search')}
          />
          <span className="k2-kbd" style={{ marginLeft: 0 }}>ESC</span>
        </div>

        <div className="k2-cp-l" ref={listRef}>
          {hits.length === 0 && (
            <div className="k2-cp-empty">No matches for “{q}”</div>
          )}
          {hits.map((c, n) => {
            const Ic = X[c.icon] || X.Doc;
            const head = c.groupKey !== lastGroup ? (lastGroup = c.groupKey) : null;
            return (
              <React.Fragment key={`${c.to}-${n}`}>
                {head && <div className="k2-cp-g">{tr(head)}</div>}
                <div
                  className={`k2-cp-i${n === i ? ' k2-on' : ''}`}
                  data-active={n === i}
                  onMouseEnter={() => setI(n)}
                  onClick={() => run(c)}
                >
                  <span className="k2-ic"><Ic s={16} /></span>
                  {tr(c.labelKey)}
                  {n === i && <span style={{ marginLeft: 'auto', color: 'var(--n400)' }}><X.Enter s={13} /></span>}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="k2-cp-f">
          <span><span className="k2-kbd" style={{ marginLeft: 0 }}>↑↓</span> navigate</span>
          <span><span className="k2-kbd" style={{ marginLeft: 0 }}>↵</span> select</span>
          <span><span className="k2-kbd" style={{ marginLeft: 0 }}>⌘K</span> toggle</span>
        </div>
      </div>
    </div>
  );
}
